import * as React from 'react';
import { Line, Point, SystemType } from '@shared/types/tsk';
import { SYSTEM_COLORS } from '../../../utils/drawing';

interface PipeLayerProps {
    worldSize: { w: number; h: number } | null;
    lines: Line[];
    draftPoints: Point[] | null;
    cursorPos: Point | null;
    activeSystem: SystemType;
    scale: number;
    pipeWidth: number;
    nodeRadius: number;
    isFaded?: boolean;
}

const PipeLayer: React.FC<PipeLayerProps> = ({
    worldSize, lines, draftPoints, cursorPos, activeSystem,
    scale, pipeWidth, nodeRadius, isFaded
}) => {
    if (!worldSize) return null;

    return (
        <svg
            viewBox={`0 0 ${worldSize.w} ${worldSize.h}`}
            width={worldSize.w}
            height={worldSize.h}
            className={`absolute top-0 left-0 overflow-visible transition-opacity duration-300 ${isFaded ? 'opacity-40' : 'opacity-100'}`}
        >
            {lines.map((line: Line) => (
                <React.Fragment key={line.id}>
                    <polyline
                        points={line.points.map((p: Point) => `${p.x},${p.y}`).join(' ')}
                        fill="none"
                        stroke={line.color}
                        strokeWidth={pipeWidth / scale}
                        vectorEffect="non-scaling-stroke"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    {line.points.map((p: Point, i: number) => (
                        <circle
                            key={`${line.id}-${i}`}
                            data-testid="node"
                            cx={p.x}
                            cy={p.y}
                            r={nodeRadius / scale}
                            fill="white"
                            stroke={line.color}
                            strokeWidth={1 / scale}
                        />
                    ))}
                </React.Fragment>
            ))}

            {draftPoints && (
                <React.Fragment>
                    <polyline
                        points={draftPoints.map(p => `${p.x},${p.y}`).join(' ')}
                        fill="none"
                        stroke={SYSTEM_COLORS[activeSystem]}
                        strokeWidth={pipeWidth / scale}
                    />
                    {cursorPos && draftPoints.length > 0 && (
                        <line
                            x1={draftPoints[draftPoints.length - 1].x}
                            y1={draftPoints[draftPoints.length - 1].y}
                            x2={cursorPos.x}
                            y2={cursorPos.y}
                            stroke={SYSTEM_COLORS[activeSystem]}
                            strokeWidth={pipeWidth / scale}
                            opacity="0.5"
                        />
                    )}
                </React.Fragment>
            )}
        </svg>
    );
};

export default React.memo(PipeLayer);
